import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import {
  Download, Twitter, Type, Send, Trash2,
  Wand2, Undo2, Redo2, RotateCcw, Copy, ArrowUp, ArrowDown, Plus, Bold, Italic,
  AlignLeft, AlignCenter, AlignRight, User, Sticker as StickerIcon, Image as ImageIcon, LayoutTemplate, Sparkles,
} from "lucide-react";
import { ROBBED_MASCOTS } from "@/lib/assets";
import { availableGendersFor, defaultSrcFor, variantsFor } from "@/lib/mascotStudio";
import { FORMATS, BACKGROUNDS, STICKER_TYPES, TEMPLATES, TONES, INTERACTION_CAPTIONS, rnd, uid, mkText, wrapLines, drawStickerShape, pickCaption, withXHandle } from "@/lib/memeKit";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/hooks/useSettings";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ShareToXDialog from "@/components/ShareToXDialog";

const TEXT_COLORS = ["#0D0D0D", "#FFFFFF", "#00FF66", "#FFD700", "#FF3366"];

const CATEGORIES = [
  { v: "meme", l: "Meme" },
  { v: "trading_disaster", l: "Trading Disaster" },
  { v: "rug", l: "Got Rugged" },
  { v: "bad_advice", l: "Bad Advice" },
];

const TABS = [
  { id: "character", label: "Character", Icon: User },
  { id: "stickers", label: "Stickers", Icon: StickerIcon },
  { id: "text", label: "Text", Icon: Type },
  { id: "background", label: "Background", Icon: ImageIcon },
  { id: "templates", label: "Templates", Icon: LayoutTemplate },
];

export default function MemeGenerator() {
  const { user } = useAuth();
  const { data: settings } = useSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetBattleId = searchParams.get("battle");
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const imagesRef = useRef({}); // src -> { img, loaded, w, h }
  const dragRef = useRef(null);
  const measuredRef = useRef({}); // text id -> { w, h }

  const { data: catalog } = useQuery({
    queryKey: ["mascot-catalog"],
    queryFn: async () => (await api.get("/mascot/catalog")).data,
  });
  const poses = useMemo(() => {
    if (catalog?.poses?.length) return catalog.poses;
    return ROBBED_MASCOTS.map((m, i) => ({
      id: m.id, label: m.label, character: "unisex", canvasWidth: 1000, canvasHeight: 1000,
      layers: [{ id: "base", role: "base", src: m.src, order: 0 }], order: i,
    }));
  }, [catalog]);
  const characters = useMemo(() => catalog?.characters || [], [catalog]);

  // ---- staging selector (which character to add) ----
  const [mascot, setMascot] = useState(null);
  const [gender, setGender] = useState("female");
  const [charId, setCharId] = useState("__default__");
  useEffect(() => {
    if (!poses.length) return;
    setMascot((cur) => (!cur ? poses[0] : poses.find((p) => p.id === cur.id) || poses[0]));
  }, [poses]);
  const genders = useMemo(() => availableGendersFor(mascot, characters), [mascot, characters]);
  useEffect(() => {
    if (!mascot) return;
    if (genders.length && !genders.includes(gender)) setGender(genders[0]);
  }, [mascot, genders, gender]);
  const variants = useMemo(() => variantsFor(mascot, gender, characters), [mascot, gender, characters]);
  useEffect(() => {
    if (!variants.some((v) => v.id === charId)) setCharId(variants[0]?.id || "__default__");
  }, [variants, charId]);
  const pickPose = (pose) => {
    const g = availableGendersFor(pose, characters);
    setMascot(pose);
    setGender(g.includes(gender) ? gender : (g[0] || gender));
    setCharId("__default__");
  };
  const pickGender = (g) => { setGender(g); setCharId("__default__"); };
  const stagedSrc = useMemo(() => {
    const v = variants.find((x) => x.id === charId) || variants[0];
    return v?.src || defaultSrcFor(mascot, gender);
  }, [variants, charId, mascot, gender]);

  // ---- editor state ----
  const [format, setFormat] = useState("square");
  const [wmOn, setWmOn] = useState(true); // $ROBBED watermark on exported memes
  const fmt = FORMATS[format];
  const W = fmt.w, H = fmt.h;
  const [bg, setBg] = useState(BACKGROUNDS[0]);
  const [elements, setElements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("character");
  const [tick, setTick] = useState(0);
  const [surprised, setSurprised] = useState(false);
  const [tone, setTone] = useState("savage");
  const lastComboRef = useRef(null);
  // Character Interaction (Phase 2C)
  const [interactionOpen, setInteractionOpen] = useState(false);
  const [char1, setChar1] = useState({ gender: "female", poseId: "pointing", styleName: "Original" });
  const [char2, setChar2] = useState({ gender: "male", poseId: "crying", styleName: "Male Style #1" });
  const [randomized, setRandomized] = useState(false);
  const [lastPreset, setLastPreset] = useState(null);
  const lastInteractionRef = useRef(null);

  // history
  const past = useRef([]); const future = useRef([]);
  const snapshot = () => ({ elements: JSON.parse(JSON.stringify(elements)), bgId: bg.id, format });
  const [histVer, setHistVer] = useState(0);
  const pushHistory = useCallback(() => {
    past.current.push({ elements: JSON.parse(JSON.stringify(elements)), bgId: bg.id, format });
    if (past.current.length > 60) past.current.shift();
    future.current = [];
    setHistVer((v) => v + 1);
  }, [elements, bg, format]);
  const applySnap = (s) => {
    setElements(s.elements);
    setBg(BACKGROUNDS.find((b) => b.id === s.bgId) || BACKGROUNDS[0]);
    setFormat(s.format);
  };
  const undo = () => {
    if (!past.current.length) return;
    future.current.push(snapshot());
    applySnap(past.current.pop());
    setSelected(null); setHistVer((v) => v + 1);
  };
  const redo = () => {
    if (!future.current.length) return;
    past.current.push(snapshot());
    applySnap(future.current.pop());
    setSelected(null); setHistVer((v) => v + 1);
  };
  const reset = () => {
    pushHistory();
    setElements([]); setBg(BACKGROUNDS[0]); setFormat("square"); setSelected(null);
  };

  const selEl = elements.find((e) => e.id === selected) || null;

  // ---- image loading ----
  const ensureImage = useCallback((src) => {
    if (!src || imagesRef.current[src]) return;
    const entry = { img: new Image(), loaded: false, w: 0, h: 0 };
    imagesRef.current[src] = entry;
    entry.img.crossOrigin = "anonymous";
    entry.img.onload = () => {
      entry.loaded = true; entry.w = entry.img.naturalWidth; entry.h = entry.img.naturalHeight;
      // fix any character elements awaiting natural aspect
      setElements((prev) => prev.map((e) => {
        if (e.type === "character" && e.src === src && e.fitOnLoad) {
          const asp = entry.w / entry.h;
          const h = e.h; const w = h * asp;
          return { ...e, w, fitOnLoad: false };
        }
        return e;
      }));
      setTick((t) => t + 1);
    };
    entry.img.onerror = () => { entry.loaded = false; toast.error("Couldn't load that character."); setTick((t) => t + 1); };
    entry.img.src = src;
  }, []);
  useEffect(() => { ensureImage(stagedSrc); }, [stagedSrc, ensureImage]);
  useEffect(() => {
    elements.forEach((e) => { if (e.type === "character") ensureImage(e.src); });
  }, [elements, ensureImage]);

  // Auto-place a default character once the catalog is ready and canvas is empty.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!stagedSrc) return;
    seededRef.current = true;
    setElements([makeCharacter(stagedSrc, W, H)]);
  }, [stagedSrc, W, H]);

  function makeCharacter(src, cw, ch) {
    const entry = imagesRef.current[src];
    const targetH = ch * 0.72;
    let w = targetH, h = targetH, fitOnLoad = true;
    if (entry?.loaded && entry.w) { const asp = entry.w / entry.h; h = targetH; w = targetH * asp; fitOnLoad = false; }
    return { id: uid(), type: "character", src, x: cw / 2, y: ch / 2, w, h, rot: 0, fitOnLoad };
  }

  const addCharacter = () => {
    if (!stagedSrc) return;
    pushHistory();
    const el = makeCharacter(stagedSrc, W, H);
    setElements((prev) => [...prev, el]);
    setSelected(el.id);
    focusCanvasMobile();
  };
  const addSticker = (type) => {
    pushHistory();
    const sz = Math.round(Math.min(W, H) * 0.22);
    const el = { id: uid(), type: "sticker", stickerType: type, x: W / 2, y: H / 2, w: sz, h: sz, rot: 0 };
    setElements((prev) => [...prev, el]); setSelected(el.id); focusCanvasMobile();
  };
  const addText = (partial) => {
    pushHistory();
    const el = mkText({ x: W / 2, y: H / 2, w: W * 0.9, fontSize: Math.round(W * 0.08), ...partial });
    setElements((prev) => [...prev, el]); setSelected(el.id); focusCanvasMobile();
  };
  const applyTemplate = (tpl) => {
    pushHistory();
    const texts = tpl.make(W, H);
    setElements((prev) => [...prev.filter((e) => e.type !== "text"), ...texts]);
    setSelected(texts[0]?.id || null); focusCanvasMobile();
  };

  const focusCanvasMobile = () => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      wrapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // ---- Character Interaction (Phase 2C) — local presets, NO AI ----
  const trackEvent = (name, meta) => { api.post("/analytics/event", { name, meta: meta || {} }).catch(() => {}); };
  useEffect(() => { trackEvent("meme_generator_opened"); }, []); // eslint-disable-line
  const INTERACTIONS = [
    { id: "faceoff", emoji: "⚔️", label: "Face-Off", poses: ["pointing", "stare"], front: "b", a: { x: .29, y: .58, s: .62, rot: -4 }, b: { x: .71, y: .58, s: .62, rot: 4 } },
    { id: "argument", emoji: "😤", label: "Argument", poses: ["pointing", "pointing"], front: "b", a: { x: .30, y: .58, s: .60, rot: -6 }, b: { x: .70, y: .58, s: .60, rot: 6 } },
    { id: "point_react", emoji: "👉", label: "Point & React", poses: ["pointing", "stare"], front: "b", a: { x: .30, y: .60, s: .60, rot: 0 }, b: { x: .72, y: .60, s: .55, rot: 0 } },
    { id: "laugh", emoji: "😂", label: "Laugh Together", poses: ["jump", "jump"], front: "b", a: { x: .34, y: .60, s: .58, rot: -3 }, b: { x: .66, y: .60, s: .58, rot: 3 } },
    { id: "comfort", emoji: "😭", label: "Comfort", poses: ["crying", "stare"], front: "b", a: { x: .40, y: .62, s: .60, rot: 0 }, b: { x: .64, y: .60, s: .56, rot: 0 } },
    { id: "winner_loser", emoji: "🏆", label: "Winner / Loser", poses: ["jump", "crying"], front: "a", a: { x: .36, y: .55, s: .72, rot: 0 }, b: { x: .74, y: .66, s: .48, rot: 0 } },
    { id: "shock", emoji: "😱", label: "Shock / Reaction", poses: ["stare", "throne"], front: "a", a: { x: .30, y: .60, s: .60, rot: 0 }, b: { x: .72, y: .60, s: .56, rot: 0 } },
  ];
  const resolveSrc = (poseId, gender, styleName) => {
    const p = poses.find((x) => x.id === poseId) || poses[0];
    const vs = variantsFor(p, gender, characters);
    const v = vs.find((x) => x.name === styleName) || vs[0];
    return v?.src || defaultSrcFor(p, gender);
  };
  const buildCharAt = (src, xf, yf, hf, rot) => {
    ensureImage(src);
    const entry = imagesRef.current[src];
    const targetH = H * hf;
    let w = targetH, h = targetH, fitOnLoad = true;
    if (entry?.loaded && entry.w) { const asp = entry.w / entry.h; h = targetH; w = targetH * asp; fitOnLoad = false; }
    return { id: uid(), type: "character", src, x: W * xf, y: H * yf, w, h, rot: rot || 0, fitOnLoad };
  };
  const applyInteraction = (p) => {
    pushHistory();
    trackEvent("character_interaction_preset_selected", { preset: p.id });
    const c1 = { ...char1, poseId: p.poses[0] || char1.poseId };
    const c2 = { ...char2, poseId: p.poses[1] || char2.poseId };
    setChar1(c1); setChar2(c2);
    const A = buildCharAt(resolveSrc(c1.poseId, c1.gender, c1.styleName), p.a.x, p.a.y, p.a.s, p.a.rot);
    const B = buildCharAt(resolveSrc(c2.poseId, c2.gender, c2.styleName), p.b.x, p.b.y, p.b.s, p.b.rot);
    const ordered = p.front === "a" ? [B, A] : [A, B]; // back first, front last
    setElements((prev) => [...ordered, ...prev.filter((e) => e.type !== "character")]);
    setSelected(null);
    trackEvent("character_interaction_created", { preset: p.id });
    trackEvent("meme_created", { source: "interaction_preset", preset: p.id });
    setLastPreset(p.id);
    focusCanvasMobile();
  };
  const swapChars = () => {
    pushHistory();
    trackEvent("character_interaction_swap", {});
    setChar1(char2); setChar2(char1);
    setElements((prev) => {
      const idxs = prev.map((e, i) => (e.type === "character" ? i : -1)).filter((i) => i >= 0);
      if (idxs.length < 2) return prev;
      const arr = [...prev]; const i = idxs[0], j = idxs[1];
      const si = arr[i].src, sj = arr[j].src;
      const fix = (el, src) => {
        const en = imagesRef.current[src]; ensureImage(src);
        if (en?.loaded && en.w) return { ...el, src, w: el.h * (en.w / en.h), fitOnLoad: false };
        return { ...el, src, fitOnLoad: true };
      };
      arr[i] = fix(arr[i], sj); arr[j] = fix(arr[j], si);
      return arr;
    });
  };
  const randomInteraction = () => {
    if (!randomized) trackEvent("random_interaction_open", {});
    let build = null;
    for (let i = 0; i < 10; i++) {
      const p = rnd(INTERACTIONS);
      const g1 = rnd(["female", "male"]), g2 = rnd(["female", "male"]);
      const pose1 = poses.find((x) => x.id === p.poses[0]) || poses[0];
      const pose2 = poses.find((x) => x.id === p.poses[1]) || poses[0];
      const v1 = variantsFor(pose1, g1, characters), v2 = variantsFor(pose2, g2, characters);
      if (!v1.length || !v2.length) continue;
      const st1 = rnd(v1).name, st2 = rnd(v2).name;
      const cap = rnd(INTERACTION_CAPTIONS[p.id] || ["WE GOT ROBBED."]);
      const stk = Math.random() < 0.5 ? rnd(STICKER_TYPES).type : null;
      const sig = `${p.id}|${g1}|${st1}|${g2}|${st2}|${cap}|${stk}`;
      if (sig === lastInteractionRef.current && i < 9) continue;
      build = { p, g1, g2, st1, st2, cap, stk, pose1, pose2, sig }; break;
    }
    if (!build) return;
    pushHistory();
    const { p } = build;
    setChar1({ gender: build.g1, poseId: build.pose1.id, styleName: build.st1 });
    setChar2({ gender: build.g2, poseId: build.pose2.id, styleName: build.st2 });
    const A = buildCharAt(resolveSrc(build.pose1.id, build.g1, build.st1), p.a.x, p.a.y, p.a.s, p.a.rot);
    const B = buildCharAt(resolveSrc(build.pose2.id, build.g2, build.st2), p.b.x, p.b.y, p.b.s, p.b.rot);
    const els = p.front === "a" ? [B, A] : [A, B];
    if (build.stk) {
      const sz = Math.round(Math.min(W, H) * 0.2);
      els.push({ id: uid(), type: "sticker", stickerType: build.stk, x: W * 0.16, y: H * 0.2, w: sz, h: sz, rot: Math.round(Math.random() * 20 - 10) });
    }
    els.push(mkText({ text: build.cap, x: W / 2, y: H * 0.9, w: W * 0.9, fontSize: Math.round(W * 0.07) }));
    setElements(els);
    setSelected(null);
    lastInteractionRef.current = build.sig;
    trackEvent("random_interaction_created", { preset: p.id });
    trackEvent("meme_created", { source: "random_interaction", preset: p.id });
    if (randomized) trackEvent("random_interaction_reroll", { preset: p.id });
    setRandomized(true);
    setLastPreset(p.id);
    focusCanvasMobile();
  };
  const addMatchingCaption = () => {
    if (!lastPreset) return;
    pushHistory();
    const cap = rnd(INTERACTION_CAPTIONS[lastPreset] || ["WE GOT ROBBED."]);
    const el = mkText({ text: cap, x: W / 2, y: H * 0.9, w: W * 0.9, fontSize: Math.round(W * 0.07) });
    setElements((prev) => [...prev, el]); setSelected(el.id);
    trackEvent("interaction_caption_added", { preset: lastPreset });
    focusCanvasMobile();
  };
  const randomInteractionPost = () => { trackEvent("random_interaction_post_open", {}); openPost(); };

  // ---- Surprise Me (Phase 2A) — builds a full random meme with the existing element system ----
  const buildSurprise = () => {
    // gender-strict: pick pose → its available genders → variant for that gender
    for (let i = 0; i < 12; i++) {
      const pose = rnd(poses);
      const gAvail = availableGendersFor(pose, characters);
      if (!gAvail.length) continue;
      const g = rnd(gAvail);
      const vars = variantsFor(pose, g, characters);
      const variant = vars.length ? rnd(vars) : null;
      const src = variant?.src || defaultSrcFor(pose, g);
      if (!src) continue;
      const bgSel = rnd(BACKGROUNDS);
      const tpl = rnd(TEMPLATES);
      const stickerType = rnd(STICKER_TYPES).type;
      const texts = tpl.make(W, H);
      const caption = pickCaption(tone, texts.length, pose.id);
      texts.forEach((tx, idx) => { tx.text = caption[idx] ?? caption[caption.length - 1] ?? tx.text; });
      const sig = `${g}|${pose.id}|${variant?.id}|${bgSel.id}|${tpl.id}|${stickerType}|${caption.join("~")}`;
      if (sig === lastComboRef.current && i < 11) continue; // avoid immediate repeat
      return { src, bgSel, stickerType, texts, sig };
    }
    return null;
  };
  const surpriseMe = () => {
    const s = buildSurprise();
    if (!s) { toast.error("Couldn't roll a meme — try again."); return; }
    ensureImage(s.src);
    pushHistory();
    const char = makeCharacter(s.src, W, H);
    const sz = Math.round(Math.min(W, H) * 0.2);
    const sticker = { id: uid(), type: "sticker", stickerType: s.stickerType, x: W * 0.74, y: H * 0.3, w: sz, h: sz, rot: Math.round(Math.random() * 30 - 15) };
    setBg(s.bgSel);
    setElements([char, sticker, ...s.texts]);
    setSelected(null);
    lastComboRef.current = s.sig;
    setSurprised(true);
    trackEvent("meme_created", { source: "surprise_me", tone });
    focusCanvasMobile();
  };

  // element mutation helpers (no history — used mid-gesture / live edits)
  const patchEl = (id, patch) => setElements((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const deleteEl = (id) => { pushHistory(); setElements((prev) => prev.filter((e) => e.id !== id)); setSelected(null); };
  const duplicateEl = (id) => {
    pushHistory();
    setElements((prev) => {
      const e = prev.find((x) => x.id === id); if (!e) return prev;
      const copy = { ...e, id: uid(), x: e.x + 40, y: e.y + 40 };
      return [...prev, copy];
    });
  };
  const layer = (id, dir) => {
    pushHistory();
    setElements((prev) => {
      const i = prev.findIndex((e) => e.id === id); if (i < 0) return prev;
      const j = dir === "up" ? Math.min(prev.length - 1, i + 1) : Math.max(0, i - 1);
      if (i === j) return prev;
      const arr = [...prev]; const [it] = arr.splice(i, 1); arr.splice(j, 0, it); return arr;
    });
  };

  // ---- rendering ----
  const draw = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = bg.value; ctx.fillRect(0, 0, W, H);

    elements.forEach((e) => {
      ctx.save();
      ctx.translate(e.x, e.y);
      if (e.rot) ctx.rotate((e.rot * Math.PI) / 180);
      if (e.type === "character") {
        const entry = imagesRef.current[e.src];
        if (entry?.loaded && entry.img.naturalWidth) {
          ctx.drawImage(entry.img, -e.w / 2, -e.h / 2, e.w, e.h);
        }
      } else if (e.type === "sticker") {
        drawStickerShape(ctx, e.stickerType, e.w);
      } else if (e.type === "text") {
        const weight = e.bold ? 900 : 600;
        const style = e.italic ? "italic " : "";
        ctx.font = `${style}${weight} ${e.fontSize}px Unbounded, sans-serif`;
        ctx.textBaseline = "middle";
        ctx.textAlign = e.align;
        const maxW = e.w;
        const lines = wrapLines(ctx, e.text, maxW);
        const lh = e.fontSize * 1.14;
        const blockH = lines.length * lh;
        measuredRef.current[e.id] = { w: maxW, h: blockH };
        const anchorX = e.align === "left" ? -maxW / 2 : e.align === "right" ? maxW / 2 : 0;
        let yy = -blockH / 2 + lh / 2;
        for (const l of lines) {
          if (e.shadow) { ctx.shadowColor = "rgba(0,0,0,0.55)"; ctx.shadowBlur = e.fontSize * 0.12; ctx.shadowOffsetX = e.fontSize * 0.04; ctx.shadowOffsetY = e.fontSize * 0.06; }
          if (e.stroke) { ctx.lineJoin = "round"; ctx.lineWidth = e.fontSize * 0.16; ctx.strokeStyle = e.strokeColor; ctx.strokeText(l, anchorX, yy); }
          ctx.shadowColor = "transparent"; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
          ctx.fillStyle = e.color; ctx.fillText(l, anchorX, yy);
          yy += lh;
        }
      }
      ctx.restore();
    });

    // $ROBBED watermark — drawn on top so it appears in every export (download / share / post)
    if (wmOn) {
      const fs = Math.round(W * 0.032);
      const pad = Math.round(W * 0.028);
      ctx.font = `800 ${fs}px Unbounded, sans-serif`;
      ctx.textAlign = "right"; ctx.textBaseline = "bottom";
      ctx.shadowColor = "rgba(0,0,0,0.35)"; ctx.shadowBlur = fs * 0.15; ctx.shadowOffsetY = fs * 0.05;
      ctx.fillStyle = bg.id === "black" ? "rgba(245,245,240,0.6)" : "rgba(13,13,13,0.55)";
      ctx.fillText("$ROBBED", W - pad, H - pad);
      ctx.shadowColor = "transparent"; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    }
  }, [elements, bg, W, H, wmOn]);

  useEffect(() => { draw(); }, [draw, tick, histVer]);

  // ---- interaction ----
  const toInternal = (clientX, clientY) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: (clientX - rect.left) * (W / rect.width), y: (clientY - rect.top) * (H / rect.height) };
  };
  const elSize = (e) => {
    if (e.type === "text") { const m = measuredRef.current[e.id]; return { w: (m?.w || e.w), h: (m?.h || e.fontSize) }; }
    return { w: e.w, h: e.h };
  };
  const hitTest = (p) => {
    for (let i = elements.length - 1; i >= 0; i--) {
      const e = elements[i];
      const { w, h } = elSize(e);
      const a = (-(e.rot || 0) * Math.PI) / 180;
      const dx = p.x - e.x, dy = p.y - e.y;
      const lx = dx * Math.cos(a) - dy * Math.sin(a);
      const ly = dx * Math.sin(a) + dy * Math.cos(a);
      const pad = 12;
      if (Math.abs(lx) <= w / 2 + pad && Math.abs(ly) <= h / 2 + pad) return e.id;
    }
    return null;
  };
  const onCanvasPointerDown = (e) => {
    const p = toInternal(e.clientX, e.clientY);
    const id = hitTest(p);
    setSelected(id);
    if (!id) return;
    const el = elements.find((x) => x.id === id);
    dragRef.current = { kind: "move", id, offx: p.x - el.x, offy: p.y - el.y, committed: false };
    canvasRef.current.setPointerCapture(e.pointerId);
  };
  const onCanvasPointerMove = (e) => {
    const d = dragRef.current; if (!d || d.kind !== "move") return;
    const p = toInternal(e.clientX, e.clientY);
    if (!d.committed) { pushHistory(); d.committed = true; }
    patchEl(d.id, { x: Math.max(0, Math.min(W, p.x - d.offx)), y: Math.max(0, Math.min(H, p.y - d.offy)) });
  };
  const endGesture = (e) => {
    dragRef.current = null;
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };

  // resize / rotate handle gestures (operate from element center; rotation-invariant)
  const startHandle = (kind, ev) => {
    ev.stopPropagation();
    if (!selEl) return;
    const p = toInternal(ev.clientX, ev.clientY);
    const dist = Math.hypot(p.x - selEl.x, p.y - selEl.y) || 1;
    pushHistory();
    dragRef.current = {
      kind, id: selEl.id, startDist: dist, cx: selEl.x, cy: selEl.y, isText: selEl.type === "text",
      startW: selEl.w, startH: selEl.h, startFont: selEl.fontSize,
      startRot: selEl.rot || 0, startAngle: Math.atan2(p.y - selEl.y, p.x - selEl.x),
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  };
  const handleMove = (ev) => {
    const d = dragRef.current; if (!d || !d.id) return;
    const p = toInternal(ev.clientX, ev.clientY);
    if (d.kind === "resize") {
      const dist = Math.hypot(p.x - d.cx, p.y - d.cy);
      const ratio = Math.max(0.15, dist / d.startDist);
      if (d.isText) {
        patchEl(d.id, { fontSize: Math.max(12, Math.round(d.startFont * ratio)), w: Math.max(60, d.startW * ratio) });
      } else {
        patchEl(d.id, { w: Math.max(24, d.startW * ratio), h: Math.max(24, d.startH * ratio) });
      }
    } else if (d.kind === "rotate") {
      const ang = Math.atan2(p.y - d.cy, p.x - d.cx);
      let deg = d.startRot + ((ang - d.startAngle) * 180) / Math.PI;
      deg = Math.round(deg);
      patchEl(d.id, { rot: deg });
    }
  };
  const handleUp = () => {
    dragRef.current = null;
    window.removeEventListener("pointermove", handleMove);
    window.removeEventListener("pointerup", handleUp);
  };

  // keyboard: delete / undo / redo
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if ((e.key === "Delete" || e.key === "Backspace") && selected) { e.preventDefault(); deleteEl(selected); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]); // eslint-disable-line

  // ---- export / share / post ----
  const [posting, setPosting] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [postPreview, setPostPreview] = useState(null);
  const [postForm, setPostForm] = useState({ username: "", story: "", category: "meme" });
  const download = () => {
    try {
      const url = canvasRef.current.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url; a.download = `robbed-meme.png`; a.click();
      toast.success("Meme downloaded. Go get someone robbed.");
      trackEvent("meme_downloaded", {});
    } catch { toast.error("Couldn't export. Try again."); }
  };
  const captionText = () => elements.filter((e) => e.type === "text").map((e) => e.text).filter(Boolean).join(" — ");
  const shareX = () => {
    const line = captionText();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent((line ? line + "\n\n" : "") + "$ROBBED · The People's Meme Coin")}`, "_blank");
  };
  // Share a Look — shares the current canvas (selected character + caption) as an image to X.
  // Mobile (Web Share API w/ files) = REAL image share. Desktop = honest 2-step download + open X.
  // Explicit user-initiated only; reuses the existing canvas composition. NO AI, NO auto-post.
  const [xShareOpen, setXShareOpen] = useState(false);
  const [xSharePreview, setXSharePreview] = useState(null);
  const [xShareCanNative, setXShareCanNative] = useState(false);
  const xShareBlobRef = useRef(null);
  const xShareFileRef = useRef(null);
  const xShareTextRef = useRef("");
  const shareALook = async () => {
    const line = captionText();
    xShareTextRef.current = withXHandle((line ? line + "\n\n" : "") + "$ROBBED · THEY GOT RICH. WE GOT ROBBED.", settings?.xUrl);
    let preview = null; try { preview = canvasRef.current.toDataURL("image/png"); } catch { /* noop */ }
    setXSharePreview(preview);
    let blob = null; try { blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png")); } catch { /* noop */ }
    xShareBlobRef.current = blob;
    const file = blob ? new File([blob], "robbed-look.png", { type: "image/png" }) : null;
    xShareFileRef.current = file;
    setXShareCanNative(!!(file && navigator.canShare && navigator.canShare({ files: [file] })));
    setXShareOpen(true);
  };
  const doNativeShareLook = async () => {
    try {
      await navigator.share({ files: [xShareFileRef.current], text: xShareTextRef.current });
      trackEvent("share_a_look_x", { method: "native", image: "attached" }); // verified image share
      toast.success("Shared — your meme image went with it.");
      setXShareOpen(false);
    } catch (err) {
      if (err?.name === "AbortError") return; // user cancelled the native sheet
      toast.error("Couldn't open the share sheet.");
    }
  };
  const onManualLookInitiated = () => {
    trackEvent("share_to_x_initiated", { surface: "share_a_look", image: "manual" }); // text-only, image NOT auto-attached
  };
  const submitToBattle = async () => {
    if (!user) { toast.error("Log in to enter a battle."); navigate("/login"); return; }
    let active;
    try {
      const { data } = await api.get("/battles");
      if (targetBattleId) active = data.find((b) => b.id === targetBattleId && b.status === "active");
      if (!active) active = data.find((b) => b.status === "active");
    } catch { toast.error("Couldn't load battles."); return; }
    if (!active) { toast.error("No active battle right now. Check back soon."); navigate("/battles"); return; }
    toast.loading("Entering battle…", { id: "battle-submit" });
    canvasRef.current.toBlob(async (blob) => {
      try {
        const fd = new FormData();
        fd.append("file", blob, "meme.png"); fd.append("caption", captionText());
        await api.post(`/battles/${active.id}/submit`, fd);
        trackEvent("battle_entry_submitted", { source: "meme_studio" });
        toast.success(`Entered "${active.title}". Good luck.`, { id: "battle-submit" }); navigate(`/battles/${active.id}`);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: "battle-submit" }); }
    }, "image/png");
  };
  const openPost = () => {
    if (!user) { toast.error("Log in to post to the Wall."); navigate("/login"); return; }
    setSelected(null);
    // Capture the exact current canvas composition as the preview to confirm before publishing.
    let preview = null;
    try { preview = canvasRef.current.toDataURL("image/png"); } catch { /* noop */ }
    setPostPreview(preview);
    setPostForm({ username: user?.displayName || user?.name || "", story: "", category: "meme" });
    setPostOpen(true);
  };
  const postToWall = () => {
    if (!postForm.username.trim()) { toast.error("Add a handle first."); return; }
    setPosting(true);
    canvasRef.current.toBlob(async (blob) => {
      try {
        const fd = new FormData();
        fd.append("file", blob, "meme.png"); fd.append("username", postForm.username);
        fd.append("story", postForm.story); fd.append("category", postForm.category);
        await api.post("/memes", fd);
        trackEvent("meme_posted_wall", { source: "meme_studio", category: postForm.category });
        toast.success("Posted to the Robbed Wall — pending moderation."); setPostOpen(false);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setPosting(false); }
    }, "image/png");
  };

  // ---- selection overlay geometry (percent based, format-agnostic) ----
  const overlayBox = () => {
    if (!selEl) return null;
    const { w, h } = elSize(selEl);
    return {
      leftPct: (selEl.x / W) * 100, topPct: (selEl.y / H) * 100,
      wPct: (w / W) * 100, hPct: (h / H) * 100, rot: selEl.rot || 0,
    };
  };
  const box = overlayBox();

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-10">
      <span className="tag">Robbed Mascots</span>
      <h1 className="headline text-cream text-4xl sm:text-6xl mt-4">MEME <span className="text-neon">GENERATOR</span></h1>
      <p className="font-mono text-dim mt-3 max-w-xl text-sm">
        Build your $ROBBED meme — drop characters, stickers and text, drag, resize, rotate, then download.
      </p>
      <button onClick={() => navigate("/remix")} className="btn-ghost !border-neon hover:!bg-neon mt-4 !py-2 !px-4 !text-xs inline-flex" data-testid="studio-remix-entry">
        <Sparkles className="w-4 h-4" /> Remix An Existing Meme
      </button>

      {/* mobile tab bar */}
      <div className="lg:hidden flex gap-1 mt-6 overflow-x-auto pb-1" data-testid="editor-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
            className={`flex items-center gap-1.5 whitespace-nowrap border-3 px-3 py-2 font-heading font-bold uppercase text-[11px] ${tab === id ? "border-neon bg-neon text-black" : "border-black bg-ink2 text-cream/80"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[300px_1fr_300px] gap-5 mt-5">
        {/* LEFT PANEL */}
        <div className={`space-y-5 ${tab === "character" || tab === "stickers" || tab === "text" || tab === "background" || tab === "templates" ? "" : "hidden"} lg:block`}>
          {/* desktop tab switch */}
          <div className="hidden lg:flex flex-wrap gap-1">
            {TABS.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`tab-d-${id}`}
                className={`flex items-center gap-1.5 border-3 px-2.5 py-1.5 font-heading font-bold uppercase text-[10px] ${tab === id ? "border-neon bg-neon text-black" : "border-black bg-ink2 text-cream/80 hover:border-neon"}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>

          {tab === "character" && (
            <div className="comic-card p-4 space-y-4" data-testid="panel-character">
              <div>
                <label className="font-display font-900 uppercase text-xs text-neon">Pose</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {poses.map((m) => (
                    <button key={m.id} onClick={() => pickPose(m)} data-testid={`mascot-${m.id}`}
                      className={`border-3 px-2 py-2.5 font-heading font-bold uppercase text-[10px] transition-colors ${mascot?.id === m.id ? "border-neon bg-neon text-black" : "border-black bg-ink2 text-cream/80 hover:border-neon"}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {genders.length > 0 && (
                <div data-testid="gender-toggle">
                  <label className="font-display font-900 uppercase text-xs text-neon">Character</label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {["female", "male"].map((g) => {
                      const has = genders.includes(g);
                      return (
                        <button key={g} type="button" disabled={!has} onClick={() => has && pickGender(g)} data-testid={`gender-${g}`}
                          className={`border-3 px-2 py-2.5 font-heading font-bold uppercase text-[10px] transition-colors ${gender === g && has ? "border-neon bg-neon text-black" : has ? "border-black bg-ink2 text-cream/80 hover:border-neon" : "border-black/40 bg-ink2/40 text-dim/50 cursor-not-allowed"}`}>
                          {g}{!has && " · soon"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label className="font-display font-900 uppercase text-xs text-neon flex items-center gap-2"><Wand2 className="w-4 h-4" /> Style</label>
                {variants.length < 1 ? (
                  <div className="comic-card p-3 mt-2 text-center" data-testid="customize-empty">
                    <p className="font-heading font-bold uppercase text-cream text-sm">More looks soon</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 mt-2" data-testid="character-variants">
                    {variants.map((v) => (
                      <button key={v.id} onClick={() => setCharId(v.id)} title={v.name} data-testid={`character-variant-${v.id}`}
                        className={`border-3 p-1 transition-colors ${charId === v.id ? "border-neon bg-neon/10" : "border-black bg-ink2 hover:border-neon"}`}>
                        <div className="aspect-square bg-ink3 overflow-hidden flex items-center justify-center">
                          <img src={v.src} alt={v.name} className="max-w-full max-h-full object-contain" />
                        </div>
                        <span className={`block mt-1 font-heading font-bold uppercase text-[8px] truncate ${charId === v.id ? "text-neon" : "text-cream/80"}`}>{v.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={addCharacter} className="btn-neon w-full" data-testid="add-character">
                <Plus className="w-4 h-4" /> Add Character
              </button>
              <p className="font-mono text-[10px] text-dim">Add more than one to stage a scene.</p>

              <div className="border-t-3 border-black pt-4 mt-1">
                <button data-testid="interaction-toggle"
                  onClick={() => { const n = !interactionOpen; setInteractionOpen(n); if (n) trackEvent("character_interaction_open", {}); }}
                  className="w-full flex items-center justify-between font-display font-900 uppercase text-xs text-neon">
                  <span>🎭 Character Interaction</span><span>{interactionOpen ? "–" : "+"}</span>
                </button>
                {interactionOpen && (
                  <div className="mt-3 space-y-3" data-testid="interaction-panel">
                    {[["1", char1, setChar1], ["2", char2, setChar2]].map(([n, ch, set]) => {
                      const pose = poses.find((p) => p.id === ch.poseId) || poses[0];
                      const g = availableGendersFor(pose, characters);
                      const styles = variantsFor(pose, ch.gender, characters).map((v) => v.name);
                      return (
                        <div key={n} className="border-3 border-black bg-ink2 p-2 space-y-2" data-testid={`interaction-char${n}`}>
                          <span className="font-heading font-bold uppercase text-[10px] text-cream">Character {n}</span>
                          <div className="grid grid-cols-2 gap-1">
                            {["female", "male"].map((gg) => {
                              const has = g.includes(gg);
                              return (
                                <button key={gg} disabled={!has} data-testid={`ic${n}-gender-${gg}`}
                                  onClick={() => has && set({ ...ch, gender: gg, styleName: variantsFor(pose, gg, characters)[0]?.name || "Original" })}
                                  className={`border-3 px-1 py-1 font-heading font-bold uppercase text-[9px] ${ch.gender === gg && has ? "border-neon bg-neon text-black" : has ? "border-black bg-ink text-cream/80" : "border-black/40 text-dim/40 cursor-not-allowed"}`}>{gg}</button>
                              );
                            })}
                          </div>
                          <select data-testid={`ic${n}-style`} value={ch.styleName} onChange={(e) => set({ ...ch, styleName: e.target.value })}
                            className="w-full bg-ink border-3 border-black px-2 py-1.5 font-mono text-[11px] text-cream focus:outline-none focus:border-neon">
                            {styles.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <select data-testid={`ic${n}-pose`} value={ch.poseId} onChange={(e) => set({ ...ch, poseId: e.target.value })}
                            className="w-full bg-ink border-3 border-black px-2 py-1.5 font-mono text-[11px] text-cream focus:outline-none focus:border-neon">
                            {poses.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                          </select>
                        </div>
                      );
                    })}
                    <button onClick={swapChars} className="btn-ghost w-full !py-2 text-[11px]" data-testid="interaction-swap">🔄 Swap 1 ↔ 2</button>
                    <div className="grid grid-cols-1 gap-2">
                      <button onClick={randomInteraction} className="btn-neon w-full !py-2 text-[11px]" data-testid="random-interaction">🎲 Random Interaction</button>
                      {randomized && <button onClick={randomInteraction} className="btn-ghost w-full !py-2 text-[11px]" data-testid="randomize-again">🔄 Randomize Again</button>}
                      {randomized && <button onClick={randomInteractionPost} className="btn-ghost w-full !py-2 text-[11px] !border-gold hover:!bg-gold" data-testid="random-interaction-post">🚀 Random Interaction → Post</button>}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {INTERACTIONS.map((p) => (
                        <button key={p.id} onClick={() => applyInteraction(p)} data-testid={`interaction-${p.id}`}
                          className="border-3 border-black bg-ink2 text-cream/90 hover:border-neon px-2 py-2 font-heading font-bold uppercase text-[9px] transition-colors">{p.emoji} {p.label}</button>
                      ))}
                    </div>
                    {lastPreset && (
                      <button onClick={addMatchingCaption} className="btn-ghost w-full !py-2 text-[11px]" data-testid="add-matching-caption">📝 Add Matching Caption</button>
                    )}
                    <p className="font-mono text-[10px] text-dim">Presets place two characters — you can still move, resize, rotate & re-pose each one.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "stickers" && (
            <div className="comic-card p-4" data-testid="panel-stickers">
              <label className="font-display font-900 uppercase text-xs text-neon">Stickers</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {STICKER_TYPES.map(({ type, label, Icon }) => (
                  <button key={type} onClick={() => addSticker(type)} data-testid={`sticker-${type}`}
                    className="flex items-center gap-1.5 border-3 border-black bg-ink2 text-cream/90 hover:border-neon px-3 py-2.5 font-heading font-bold uppercase text-[10px] transition-colors">
                    <Icon className="w-4 h-4 text-neon" /> {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {tab === "text" && (
            <div className="comic-card p-4 space-y-3" data-testid="panel-text">
              <label className="font-display font-900 uppercase text-xs text-neon">Text</label>
              <button onClick={() => addText({ text: "TEXT" })} className="btn-neon w-full" data-testid="add-text"><Plus className="w-4 h-4" /> Add Text</button>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => addText({ text: "TOP TEXT", y: H * 0.1 })} className="btn-ghost text-[10px]" data-testid="add-top-text">Top Text</button>
                <button onClick={() => addText({ text: "BOTTOM TEXT", y: H * 0.9 })} className="btn-ghost text-[10px]" data-testid="add-bottom-text">Bottom Text</button>
              </div>
              <p className="font-mono text-[10px] text-dim">Select any text on the canvas to edit its style in the right panel.</p>
            </div>
          )}

          {tab === "background" && (
            <div className="comic-card p-4" data-testid="panel-background">
              <label className="font-display font-900 uppercase text-xs text-neon">Background</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {BACKGROUNDS.map((b) => (
                  <button key={b.id} onClick={() => { pushHistory(); setBg(b); }} data-testid={`bg-${b.id}`}
                    className={`h-14 border-3 ${bg.id === b.id ? "border-neon" : "border-black"}`} style={{ background: b.value }} title={b.label}>
                    <span className="sr-only">{b.label}</span>
                  </button>
                ))}
              </div>
              <p className="font-mono text-[10px] text-dim mt-2">More backgrounds coming in a later phase.</p>
            </div>
          )}

          {tab === "templates" && (
            <div className="comic-card p-4" data-testid="panel-templates">
              <label className="font-display font-900 uppercase text-xs text-neon">Meme Templates</label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {TEMPLATES.map((t) => (
                  <button key={t.id} onClick={() => applyTemplate(t)} data-testid={`template-${t.id}`}
                    className="border-3 border-black bg-ink2 text-cream/90 hover:border-neon px-3 py-2.5 font-heading font-bold uppercase text-[11px] text-left transition-colors">
                    {t.label}
                  </button>
                ))}
              </div>
              <p className="font-mono text-[10px] text-dim mt-2">Applies a text layout — you can still edit everything.</p>
            </div>
          )}
        </div>

        {/* CENTER — CANVAS */}
        <div className="space-y-3">
          {/* Surprise Me */}
          <div className="comic-card p-3 sm:p-4 space-y-3" data-testid="surprise-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-display font-900 uppercase text-neon text-sm">🎲 Surprise Me</p>
                <p className="font-mono text-[10px] text-dim">One tap = a full random $ROBBED meme.</p>
              </div>
              <div className="flex items-center gap-1.5" data-testid="tone-toggle">
                {TONES.map((t) => (
                  <button key={t.id} onClick={() => setTone(t.id)} data-testid={`tone-${t.id}`} title={t.short}
                    className={`border-3 px-2 py-1.5 font-heading font-bold uppercase text-[10px] transition-colors ${tone === t.id ? "border-neon bg-neon text-black" : "border-black bg-ink2 text-cream/80 hover:border-neon"}`}>
                    {t.emoji} {t.short}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={surpriseMe} className="btn-neon w-full !text-base" data-testid="surprise-me">🎲 Surprise Me</button>
            {surprised && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="surprise-actions">
                <button onClick={surpriseMe} className="btn-ghost !py-2 text-[11px]" data-testid="shuffle-again">🔄 Shuffle</button>
                <button onClick={() => { setSurprised(false); focusCanvasMobile(); }} className="btn-ghost !py-2 text-[11px]" data-testid="edit-meme">✏️ Edit</button>
                <button onClick={shareX} className="btn-ghost !py-2 text-[11px]" data-testid="surprise-share">📤 Share</button>
                <button onClick={openPost} className="btn-ghost !py-2 text-[11px] !border-gold hover:!bg-gold" data-testid="surprise-post">🚀 Post</button>
              </div>
            )}
          </div>

          {/* toolbar */}
          <div className="comic-card p-2 flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-1.5">
              <button onClick={undo} disabled={!past.current.length} className="tool-btn" data-testid="undo-btn" title="Undo"><Undo2 className="w-4 h-4" /></button>
              <button onClick={redo} disabled={!future.current.length} className="tool-btn" data-testid="redo-btn" title="Redo"><Redo2 className="w-4 h-4" /></button>
              <button onClick={reset} className="tool-btn" data-testid="reset-btn" title="Reset"><RotateCcw className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-1.5" data-testid="format-toggle">
              {Object.values(FORMATS).map((f) => (
                <button key={f.id} onClick={() => { pushHistory(); setFormat(f.id); }} data-testid={`format-${f.id}`}
                  className={`border-3 px-2.5 py-1.5 font-heading font-bold uppercase text-[10px] ${format === f.id ? "border-neon bg-neon text-black" : "border-black bg-ink2 text-cream/80 hover:border-neon"}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setWmOn((v) => !v)} data-testid="meme-watermark-toggle"
                title="$ROBBED watermark"
                className={`border-3 px-2.5 py-1.5 font-heading font-bold uppercase text-[10px] ${wmOn ? "border-neon bg-neon text-black" : "border-black bg-ink2 text-cream/80 hover:border-neon"}`}>
                $ROBBED
              </button>
              <button onClick={download} className="btn-neon !py-2 text-[12px]" data-testid="meme-download"><Download className="w-4 h-4" /> Download PNG</button>
            </div>
          </div>

          <div className="comic-card p-3 sm:p-5 flex items-center justify-center bg-ink3">
            <div ref={wrapRef} className="relative w-full" style={{ maxWidth: W >= H ? 640 : 420 }}>
              <canvas
                ref={canvasRef} width={W} height={H} data-testid="meme-canvas"
                onPointerDown={onCanvasPointerDown} onPointerMove={onCanvasPointerMove}
                onPointerUp={endGesture} onPointerCancel={endGesture}
                className="w-full border-3 border-neon touch-none cursor-move block"
                style={{ aspectRatio: `${W} / ${H}` }}
              />
              {/* selection overlay */}
              {box && (
                <div className="absolute pointer-events-none" style={{ left: `${box.leftPct}%`, top: `${box.topPct}%`, width: `${box.wPct}%`, height: `${box.hPct}%`, transform: `translate(-50%, -50%) rotate(${box.rot}deg)` }} data-testid="selection-box">
                  <div className="absolute inset-0 border-2 border-neon border-dashed" />
                  {/* rotate handle */}
                  <div onPointerDown={(e) => startHandle("rotate", e)} data-testid="handle-rotate"
                    className="absolute left-1/2 -top-7 -translate-x-1/2 w-5 h-5 rounded-full bg-neon border-2 border-black cursor-grab pointer-events-auto" title="Rotate" />
                  {/* resize handle */}
                  <div onPointerDown={(e) => startHandle("resize", e)} data-testid="handle-resize"
                    className="absolute -right-2.5 -bottom-2.5 w-5 h-5 bg-gold border-2 border-black cursor-nwse-resize pointer-events-auto" title="Resize" />
                </div>
              )}
            </div>
          </div>

          {/* share / post row */}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={shareALook} className="btn-neon col-span-2 !text-base" data-testid="meme-share-a-look"><Twitter className="w-4 h-4" /> Share a Look on X</button>
            <button onClick={shareX} className="btn-ghost" data-testid="meme-share"><Twitter className="w-4 h-4" /> Share Caption</button>
            <button onClick={openPost} className="btn-ghost !border-gold hover:!bg-gold" data-testid="meme-post-open"><Send className="w-4 h-4" /> Post To Wall</button>
            <button onClick={submitToBattle} className={`btn-ghost col-span-2 !border-grape hover:!bg-grape hover:!text-cream ${targetBattleId ? "!bg-grape !text-cream animate-pulse" : ""}`} data-testid="meme-battle-submit"><Send className="w-4 h-4" /> {targetBattleId ? "Enter This Battle" : "Submit To Meme Battle"}</button>
          </div>
        </div>

        {/* RIGHT PANEL — selected element */}
        <div className="lg:block">
          <div className="comic-card p-4 space-y-4" data-testid="inspector">
            <label className="font-display font-900 uppercase text-xs text-neon">Selected Element</label>
            {!selEl ? (
              <p className="font-mono text-[11px] text-dim" data-testid="inspector-empty">Tap an element on the canvas to move, resize, rotate, reorder or delete it.</p>
            ) : (
              <div className="space-y-4" data-testid="inspector-controls">
                <div className="flex items-center justify-between">
                  <span className="font-heading font-bold uppercase text-[11px] text-cream">{selEl.type}</span>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => duplicateEl(selEl.id)} className="tool-btn" data-testid="dup-btn" title="Duplicate"><Copy className="w-4 h-4" /></button>
                    <button onClick={() => layer(selEl.id, "up")} className="tool-btn" data-testid="layer-up-btn" title="Forward"><ArrowUp className="w-4 h-4" /></button>
                    <button onClick={() => layer(selEl.id, "down")} className="tool-btn" data-testid="layer-down-btn" title="Backward"><ArrowDown className="w-4 h-4" /></button>
                    <button onClick={() => deleteEl(selEl.id)} className="tool-btn !text-danger" data-testid="delete-btn" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                {selEl.type === "text" && (
                  <>
                    <textarea rows={2} value={selEl.text} onFocus={pushHistory}
                      onChange={(e) => patchEl(selEl.id, { text: e.target.value })}
                      data-testid="text-edit"
                      className="w-full bg-ink border-3 border-black px-3 py-2 font-mono text-sm text-cream focus:outline-none focus:border-neon resize-none" />
                    <div className="flex items-center gap-2">
                      <button onClick={() => patchEl(selEl.id, { bold: !selEl.bold })} className={`tool-btn ${selEl.bold ? "!border-neon !text-neon" : ""}`} data-testid="text-bold"><Bold className="w-4 h-4" /></button>
                      <button onClick={() => patchEl(selEl.id, { italic: !selEl.italic })} className={`tool-btn ${selEl.italic ? "!border-neon !text-neon" : ""}`} data-testid="text-italic"><Italic className="w-4 h-4" /></button>
                      <button onClick={() => patchEl(selEl.id, { align: "left" })} className={`tool-btn ${selEl.align === "left" ? "!border-neon !text-neon" : ""}`} data-testid="text-align-left"><AlignLeft className="w-4 h-4" /></button>
                      <button onClick={() => patchEl(selEl.id, { align: "center" })} className={`tool-btn ${selEl.align === "center" ? "!border-neon !text-neon" : ""}`} data-testid="text-align-center"><AlignCenter className="w-4 h-4" /></button>
                      <button onClick={() => patchEl(selEl.id, { align: "right" })} className={`tool-btn ${selEl.align === "right" ? "!border-neon !text-neon" : ""}`} data-testid="text-align-right"><AlignRight className="w-4 h-4" /></button>
                    </div>
                    <div>
                      <span className="font-mono text-[10px] text-dim uppercase">Font size — {selEl.fontSize}px</span>
                      <input type="range" min="24" max="240" value={selEl.fontSize} onChange={(e) => patchEl(selEl.id, { fontSize: +e.target.value })} className="w-full accent-neon" data-testid="text-fontsize" />
                    </div>
                    <div>
                      <span className="font-mono text-[10px] text-dim uppercase">Text color</span>
                      <div className="flex gap-2 mt-1">
                        {TEXT_COLORS.map((c) => (
                          <button key={c} onClick={() => patchEl(selEl.id, { color: c })} className={`w-8 h-8 border-3 ${selEl.color === c ? "border-cream" : "border-black"}`} style={{ background: c }} data-testid={`text-color-${c}`} />
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 font-mono text-[11px] text-cream">
                        <input type="checkbox" checked={selEl.stroke} onChange={(e) => patchEl(selEl.id, { stroke: e.target.checked })} className="w-4 h-4 accent-neon" data-testid="text-stroke" /> Outline
                      </label>
                      <label className="flex items-center gap-2 font-mono text-[11px] text-cream">
                        <input type="checkbox" checked={selEl.shadow} onChange={(e) => patchEl(selEl.id, { shadow: e.target.checked })} className="w-4 h-4 accent-neon" data-testid="text-shadow" /> Shadow
                      </label>
                    </div>
                  </>
                )}

                {selEl.type !== "text" && (
                  <div>
                    <span className="font-mono text-[10px] text-dim uppercase">Size</span>
                    <input type="range" min={Math.round(Math.min(W, H) * 0.05)} max={Math.round(Math.max(W, H) * 1.1)} value={Math.round(selEl.w)}
                      onChange={(e) => { const nw = +e.target.value; const asp = selEl.h / selEl.w; patchEl(selEl.id, { w: nw, h: nw * asp }); }}
                      className="w-full accent-neon" data-testid="size-slider" />
                  </div>
                )}

                <div>
                  <span className="font-mono text-[10px] text-dim uppercase">Rotation — {Math.round(selEl.rot || 0)}°</span>
                  <input type="range" min="-180" max="180" value={selEl.rot || 0} onChange={(e) => patchEl(selEl.id, { rot: +e.target.value })} className="w-full accent-neon" data-testid="rotation-slider" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="font-mono text-[10px] text-dim uppercase">X</span>
                    <input type="range" min="0" max={W} value={Math.round(selEl.x)} onChange={(e) => patchEl(selEl.id, { x: +e.target.value })} className="w-full accent-neon" data-testid="pos-x" />
                  </div>
                  <div>
                    <span className="font-mono text-[10px] text-dim uppercase">Y</span>
                    <input type="range" min="0" max={H} value={Math.round(selEl.y)} onChange={(e) => patchEl(selEl.id, { y: +e.target.value })} className="w-full accent-neon" data-testid="pos-y" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={postOpen} onOpenChange={setPostOpen}>
        <DialogContent className="bg-ink2 border-3 border-black text-cream max-w-md">
          <DialogHeader><DialogTitle className="font-display font-900 uppercase text-neon">Post To The Wall</DialogTitle></DialogHeader>
          <DialogDescription className="font-mono text-[11px] text-dim -mt-1">Review the preview, add your handle and caption, then confirm to publish.</DialogDescription>
          <div className="space-y-4 mt-2">
            {postPreview && (
              <div className="border-3 border-black bg-ink3 p-2" data-testid="post-preview">
                <img src={postPreview} alt="Meme preview" className="w-full max-h-64 object-contain" />
                <p className="font-mono text-[10px] text-dim mt-1 text-center">Preview — this exact composition will be posted.</p>
              </div>
            )}
            <input data-testid="post-username" value={postForm.username} placeholder="Anon handle"
              onChange={(e) => setPostForm({ ...postForm, username: e.target.value })}
              className="w-full bg-ink border-3 border-black px-3 py-2.5 font-mono text-sm text-cream focus:outline-none focus:border-neon" />
            <Select value={postForm.category} onValueChange={(v) => setPostForm({ ...postForm, category: v })}>
              <SelectTrigger className="w-full bg-ink border-3 border-black text-cream font-mono text-sm" data-testid="post-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-ink2 border-3 border-black text-cream">
                {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <textarea data-testid="post-caption" rows={3} value={postForm.story} placeholder="Caption (optional)"
              onChange={(e) => setPostForm({ ...postForm, story: e.target.value })}
              className="w-full bg-ink border-3 border-black px-3 py-2.5 font-mono text-sm text-cream focus:outline-none focus:border-neon resize-none" />
            <button onClick={postToWall} disabled={posting} className="btn-neon w-full disabled:opacity-50" data-testid="post-submit">
              {posting ? "Posting..." : "Confirm & Post Meme"}
            </button>
            <p className="font-mono text-[10px] text-dim">Posts are moderated before appearing on the Wall.</p>
          </div>
        </DialogContent>
      </Dialog>

      <ShareToXDialog
        open={xShareOpen} onOpenChange={setXShareOpen}
        preview={xSharePreview} canNative={xShareCanNative}
        blob={xShareBlobRef.current} filename="robbed-look.png" text={xShareTextRef.current}
        onNativeShare={doNativeShareLook} onManualInitiated={onManualLookInitiated}
        testidPrefix="look-share" title="Share a Look on X"
      />
    </div>
  );
}
